import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import NotFound from "@/pages/not-found";
import Shell from "@/components/layout/Shell";
import type { ComponentType } from "react";
import { useState, useEffect } from "react";
import { I18nextProvider } from "react-i18next";
import i18n, { initI18n } from "@/lib/i18n";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import AssessmentsList from "@/pages/AssessmentsList";
import Workflow from "@/pages/Workflow";
import Dashboard from "@/pages/Dashboard";
import Report from "@/pages/Report";
import Reports from "@/pages/Reports";
import TextReport from "@/pages/TextReport";
import Simulator from "@/pages/Simulator";
import Profile from "@/pages/Profile";
import OnboardingInvites from "@/pages/OnboardingInvites";
import Settings from "@/pages/Settings";
import PublicReport from "@/pages/PublicReport";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminAssessments from "@/pages/admin/AdminAssessments";
import AdminWorkspaces from "@/pages/admin/AdminWorkspaces";
import AdminReports from "@/pages/admin/AdminReports";
import AdminReportDetail from "@/pages/admin/AdminReportDetail";
import AdminTranslations from "@/pages/admin/AdminTranslations";
import AdminAiUsage from "@/pages/admin/AdminAiUsage";
import AdminContacts from "@/pages/admin/AdminContacts";
import AdminSustainabilityScanLeads from "@/pages/admin/AdminSustainabilityScanLeads";
import Blog from "@/pages/blog/Blog";
import BlogEditor from "@/pages/blog/BlogEditor";
import BlogPostPreview from "@/pages/blog/BlogPostPreview";
import UnauthorizedPage from "@/pages/UnauthorizedPage";
import { AdminOnlyRoute } from "@/components/AdminOnlyRoute";
import { CompanyUserOnlyRoute } from "@/components/CompanyUserOnlyRoute";
import { WorkspaceAdminOnlyRoute } from "@/components/WorkspaceAdminOnlyRoute";
import { PlatformStaffRoute } from "@/components/PlatformStaffRoute";
import AssistantDashboard from "@/pages/assistant/AssistantDashboard";

// Protected Route Component
function ProtectedRoute({ component: Component }: { component: ComponentType }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  return <Component />;
}

function RootRoute() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/login" />;
  }

  if (user.role === "admin") {
    return <Redirect to="/admin/dashboard" />;
  }

  if (user.role === "platform_assistant") {
    return <Redirect to="/assistant/dashboard" />;
  }

  return <Redirect to={user.workspaceRole === "workspace_admin" ? "/dashboard" : "/assessments"} />;
}

function Router() {
  return (
    <Switch>
      {/* Public Routes (no Shell, no authentication) */}
      <Route path="/public/report/:token" component={PublicReport} />
      <Route path="/onboarding" component={Onboarding} />
      <Route path="/unauthorized" component={UnauthorizedPage} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password/:token" component={ResetPassword} />
      
      {/* Protected Routes (with Shell and authentication) */}
      <Shell>
        <Switch>
          <Route path="/" component={RootRoute} />
          <Route path="/login" component={Login} />
          
          {/* Company User Routes (accessible to workspace_admin and members) */}
          <Route path="/assessments">
            {() => (
              <ProtectedRoute
                component={() => (
                  <CompanyUserOnlyRoute>
                    <AssessmentsList />
                  </CompanyUserOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/workflow/:assessmentId">
            {() => (
              <ProtectedRoute
                component={() => (
                  <CompanyUserOnlyRoute>
                    <Workflow />
                  </CompanyUserOnlyRoute>
                )}
              />
            )}
          </Route>
          
          {/* Workspace Admin Only Routes */}
          <Route path="/dashboard">
            {() => (
              <ProtectedRoute
                component={() => (
                  <WorkspaceAdminOnlyRoute>
                    <Dashboard />
                  </WorkspaceAdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/reports">
            {() => (
              <ProtectedRoute
                component={() => (
                  <WorkspaceAdminOnlyRoute>
                    <Reports />
                  </WorkspaceAdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/report/text" component={TextReport} />
          <Route path="/report/:assessmentId">
            {() => (
              <ProtectedRoute
                component={() => (
                  <CompanyUserOnlyRoute>
                    <Report />
                  </CompanyUserOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/report">
            {() => (
              <ProtectedRoute
                component={() => (
                  <WorkspaceAdminOnlyRoute>
                    <Report />
                  </WorkspaceAdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/simulator">
            {() => (
              <ProtectedRoute
                component={() => (
                  <WorkspaceAdminOnlyRoute>
                    <Simulator />
                  </WorkspaceAdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/settings">
            {() => (
              <ProtectedRoute
                component={() => (
                  <WorkspaceAdminOnlyRoute>
                    <Settings />
                  </WorkspaceAdminOnlyRoute>
                )}
              />
            )}
          </Route>
          
          {/* All authenticated users */}
          <Route path="/profile">
            {() => <ProtectedRoute component={Profile} />}
          </Route>
          
          {/* Platform Admin Only Routes */}
          <Route path="/admin/OnboardingInvites">
            {() => (
              <ProtectedRoute
                component={() => (
                  <AdminOnlyRoute>
                    <OnboardingInvites />
                  </AdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/admin/dashboard">
            {() => (
              <ProtectedRoute
                component={() => (
                  <AdminOnlyRoute>
                    <AdminDashboard />
                  </AdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/admin/invitations">
            {() => (
              <ProtectedRoute
                component={() => (
                  <AdminOnlyRoute>
                    <OnboardingInvites />
                  </AdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/admin/users">
            {() => (
              <ProtectedRoute
                component={() => (
                  <AdminOnlyRoute>
                    <AdminUsers />
                  </AdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/admin/assessments">
            {() => (
              <ProtectedRoute
                component={() => (
                  <AdminOnlyRoute>
                    <AdminAssessments />
                  </AdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/admin/workspaces">
            {() => (
              <ProtectedRoute
                component={() => (
                  <AdminOnlyRoute>
                    <AdminWorkspaces />
                  </AdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/admin/reports/:assessmentId">
            {() => (
              <ProtectedRoute
                component={() => (
                  <AdminOnlyRoute>
                    <AdminReportDetail />
                  </AdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/admin/reports">
            {() => (
              <ProtectedRoute
                component={() => (
                  <AdminOnlyRoute>
                    <AdminReports />
                  </AdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/admin/translations">
            {() => (
              <ProtectedRoute
                component={() => (
                  <PlatformStaffRoute>
                    <AdminTranslations />
                  </PlatformStaffRoute>
                )}
              />
            )}
          </Route>
          <Route path="/admin/ai-usage">
            {() => (
              <ProtectedRoute
                component={() => (
                  <AdminOnlyRoute>
                    <AdminAiUsage />
                  </AdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/admin/contacts">
            {() => (
              <ProtectedRoute
                component={() => (
                  <AdminOnlyRoute>
                    <AdminContacts />
                  </AdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/admin/sustainability-scan-leads">
            {() => (
              <ProtectedRoute
                component={() => (
                  <AdminOnlyRoute>
                    <AdminSustainabilityScanLeads />
                  </AdminOnlyRoute>
                )}
              />
            )}
          </Route>
          <Route path="/blog/new">
            {() => (
              <ProtectedRoute
                component={() => (
                  <PlatformStaffRoute>
                    <BlogEditor />
                  </PlatformStaffRoute>
                )}
              />
            )}
          </Route>
          <Route path="/blog/:slug/edit">
            {() => (
              <ProtectedRoute
                component={() => (
                  <PlatformStaffRoute>
                    <BlogEditor />
                  </PlatformStaffRoute>
                )}
              />
            )}
          </Route>
          <Route path="/blog/:slug">
            {() => (
              <ProtectedRoute
                component={() => (
                  <PlatformStaffRoute>
                    <BlogPostPreview />
                  </PlatformStaffRoute>
                )}
              />
            )}
          </Route>
          <Route path="/blog">
            {() => (
              <ProtectedRoute
                component={() => (
                  <PlatformStaffRoute>
                    <Blog />
                  </PlatformStaffRoute>
                )}
              />
            )}
          </Route>
          <Route path="/assistant/dashboard">
            {() => (
              <ProtectedRoute
                component={() => (
                  <PlatformStaffRoute>
                    <AssistantDashboard />
                  </PlatformStaffRoute>
                )}
              />
            )}
          </Route>
          <Route component={NotFound} />
        </Switch>
      </Shell>
    </Switch>
  );
}

function App() {
  const [i18nReady, setI18nReady] = useState(false);

  useEffect(() => {
    initI18n().then(() => setI18nReady(true));
  }, []);

  if (!i18nReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}

export default App;
